/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { RecurringFileList } from '@back-office/http';
import { getDatetimeText, removeEmpty } from '@back-office/ui';
import { DateRange } from '@material-ui/pickers';
import endOfDay from 'date-fns/endOfDay';
import _ from 'lodash';
import { useLocalStore } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import { FlowReturn } from 'mobx-state-tree/dist/internal';
import ms from 'ms';
import qs from 'query-string';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMount, useUnmount } from 'react-use';
import {
  AlertState,
  defaultResponse,
  listItem,
  responseDataProps,
  Store,
  UploadLogsProps,
} from '.';
import {
  PAYMENT_CHECKER_O1_PERMISSION,
  PAYMENT_CHECKER_O2_PERMISSION,
  STATUS_CODE_NOT_AUTHORIZED,
  STATUS_CODE_REJECT,
  STATUS_CODE_SUCCESS,
} from '../../../constants';
import useInject from '../../../hooks/useInject';
import AuthModel from '../../../stores/auth.store';
import RecurringFileModel from '../../../stores/recurring-file.store';

type AuthModelType = Instance<typeof AuthModel>;
type RecurringFileModelType = Instance<typeof RecurringFileModel>;

type UseViewModelReturn = {
  state: Store;
  isMultiSelect: boolean;
  setIsMultiSelect: (value: boolean) => void;
  handleMultipleSelection: () => void;
  handleCloseMultipleSelection: () => void;
  handleApproveRecurringFile: (
    id: string[],
    requestedAt: string
  ) => Promise<void>;
  handleRejectRecurringFile: (
    id: string[],
    reason: string | undefined
  ) => Promise<void>;
  alertMessage: string | undefined;
  failedAlert: AlertState;
  overdueAlert: AlertState;
  tryagainAlert: AlertState;
  alertStatus: string;
  isCheckerPermission: string | undefined;
  approvedRecurringCheckLists: (data: {
    onboardBatchID: string[];
  }) => Promise<FlowReturn<any>>;
  auth: AuthModelType;
  recurringFile: RecurringFileModelType;
};

const useViewModel = ({ uploaded }: UploadLogsProps): UseViewModelReturn => {
  const { t } = useTranslation();
  const {
    getSearchRecurring,
    recurringFile,
    auth,
    setLoading,
    approvedRecurringFilePending,
    approvedRecurringFilePartial,
    rejectRecurringFile,
    downloadFileRecurring,
    approvedRecurringCheckLists,
  } = useInject((root) => root);

  const state = useLocalStore<Store>(() => ({
    date: [null, null],
    openCalendar: false,
    start: 0,
    page: 1,
    count: 20,
    key: '',
    status: '',
    process_status: '',
    mobile_no: '',
    is_multiple_selected: false,
    get query(): string {
      return qs.stringify(
        removeEmpty({
          page: this.page,
          count: 20,
          query: this.key || null,
          status: this.status || null,
          process_status: this.process_status || null,
          is_multiple_selected: String(this.is_multiple_selected) || null,
          merchantID: auth.currentUser.merchantID,
          scheduled_date_from: this.date[0]
            ? getDatetimeText(this.date[0], 'json')
            : null,
          scheduled_date_to: this.date[1]
            ? getDatetimeText(endOfDay(this.date[1]), 'json')
            : null,
        })
      );
    },
    handleOnGo() {
      this.page = 1;
      this.start = 0;
      getSearchRecurring(this.query);
    },

    handleOnChangeDatePicker(date: DateRange<Date>) {
      this.date = date;
    },

    handleOnClear(event: MouseEvent<HTMLOrSVGElement>) {
      event.stopPropagation();
      this.date = [null, null];
      this.handleOnGo();
    },
    handleOnClickDatePicker() {
      this.openCalendar = true;
    },
    handleOnCloseDatePicker() {
      this.openCalendar = false;
      this.handleOnGo();
    },

    handleOnChangePage(newPage: number) {
      this.page = newPage;
      getSearchRecurring(this.query);
    },
    handleKeySearch(searchValue: string) {
      if (this.key !== searchValue) {
        this.key = searchValue;
        this.handleOnGo();
      }
    },
    handleStatusSearch(value: string) {
      if (value === 'In queue') {
        this.status = 'Ongoing processing';
        this.process_status = 'Ready';
      } else if (value === 'Ongoing processing') {
        this.status = 'Ongoing processing';
        this.process_status = 'Processing';
      } else {
        this.status = value;
        this.process_status = '';
      }
      this.handleOnGo();
    },
    handleOnClearMultipleSelectedQuery() {
      state.is_multiple_selected = false;
    },
    async handleDownloadFile(fileName: string) {
      await downloadFileRecurring(fileName);
    },
  }));

  useMount(() => {
    getSearchRecurring(state.query);
  });

  useUnmount(() => {
    recurringFile.reset();
  });

  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | undefined>('');
  const [failedAlert, setFailedAlert] = useState<AlertState>({
    status: 'error',
    msg: '',
  });
  const [tryagainAlert, setTryAgainAlert] = useState<AlertState>({
    status: 'error',
    msg: '',
  });
  const [overdueAlert, setOverdueAlert] = useState<AlertState>({
    status: 'error',
    msg: '',
  });

  const [alertStatus, setAlertStatus] = useState<string>('Approved');

  const handleMultipleSelection = (): void => {
    setIsMultiSelect(true);
    state.is_multiple_selected = true;
    state.handleOnGo();
  };

  const handleCloseMultipleSelection = (): void => {
    setIsMultiSelect(false);
    state.is_multiple_selected = false;
    state.handleOnGo();
  };

  useEffect(() => {
    if (isMultiSelect) {
      state.handleStatusSearch('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiSelect]);

  useEffect(() => {
    if (uploaded) {
      state.handleOnGo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploaded]);

  interface GroupedIds {
    pendingId: string[];
    partialId: string[];
  }
  const groupIdsByStatus = (id: string[]): GroupedIds => {
    const groupedIds: GroupedIds = {
      pendingId: [],
      partialId: [],
    };

    recurringFile.list.forEach((file: RecurringFileList) => {
      if (id.includes(file.id) && file.status === 'Pending approval') {
        groupedIds.pendingId.push(file.id);
      } else if (id.includes(file.id) && file.status === 'Partial approved') {
        groupedIds.partialId.push(file.id);
      }
    });

    return groupedIds;
  };

  const isResponseDataKey = (key: string): key is keyof responseDataProps =>
    key in defaultResponse;

  const resMerged = (
    resPending: responseDataProps | null,
    resPartial: responseDataProps | null
  ): responseDataProps => {
    // If either resPending or resPartial is null, return the other one
    if (!resPending) return resPartial || defaultResponse;
    if (!resPartial) return resPending;

    const merged: responseDataProps = { ...defaultResponse };

    for (const key in resPending) {
      if (isResponseDataKey(key)) {
        const pendingValue = resPending[key];
        const partialValue = resPartial[key];
        if (
          typeof pendingValue === 'number' &&
          typeof partialValue === 'number'
        ) {
          // If both values are numbers, add them
          merged[key] = pendingValue + partialValue;
        } else if (Array.isArray(pendingValue) && Array.isArray(partialValue)) {
          // If both values are arrays, concatenate them
          merged[key] = [...(pendingValue || []), ...(partialValue || [])] as
            | number
            | (number & listItem[]);
        } else {
          // Otherwise, use the value from resPending (or you can choose resPartial)
          merged[key] = (pendingValue ??
            partialValue ??
            defaultResponse[key]) as number | (number & listItem[]);
        }
      }
    }
    return merged;
  };

  const handleAlerts = (data: responseDataProps): void => {
    const {
      approve_success_count,
      is_already_approved_count,
      is_fail_to_approve_count,
      is_overdue_count,
    } = data;

    if (approve_success_count > 0) {
      setAlertMessage(
        approve_success_count > 1
          ? t('payment_file.approved_multiple_alert', {
              files: approve_success_count,
            })
          : t('payment_file.approved_alert')
      );
    }

    if (is_fail_to_approve_count > 0) {
      setFailedAlert({
        ...failedAlert,
        msg:
          is_fail_to_approve_count > 1
            ? t('payment_file.failed_approved_multiple_alert', {
                files: is_fail_to_approve_count,
              })
            : t('payment_file.failed_approved_alert'),
      });
    }

    if (is_already_approved_count > 0) {
      setTryAgainAlert({
        ...tryagainAlert,
        msg: t(
          is_already_approved_count > 1
            ? 'payment_file.already_approved_or_rejected_alert_multiple'
            : 'payment_file.already_approved_or_rejected_alert_single'
        ),
      });
    }

    if (is_overdue_count > 0) {
      setOverdueAlert({
        ...overdueAlert,
        msg: t('payment_file.overdue_alert'),
      });
    }
  };

  const handleApproveRecurringFile = async (
    id: string[],
    requestedAt: string
  ): Promise<void> => {
    if (id) {
      setLoading(true);
      setAlertStatus('Approved');
      const { pendingId, partialId } = groupIdsByStatus(id);
      let pendingResponse: responseDataProps | null = null;
      let partialResponse: responseDataProps | null = null;
      try {
        if (!_.isEmpty(pendingId)) {
          pendingResponse = await approvedRecurringFilePending(
            pendingId,
            requestedAt
          );
        }
        if (!_.isEmpty(partialId)) {
          partialResponse = await approvedRecurringFilePartial(
            partialId,
            requestedAt
          );
        }
        const mergedResponse = resMerged(pendingResponse, partialResponse);
        handleAlerts(mergedResponse);
      } catch (error) {
        if (error !== STATUS_CODE_NOT_AUTHORIZED) {
          await getSearchRecurring(state.query);
        }
      } finally {
        setIsMultiSelect(false);
        state.handleOnClearMultipleSelectedQuery();
        await getSearchRecurring(state.query);
        setLoading(false);
      }
    }
  };

  const handleRejectRecurringFile = async (
    id: string[],
    reason: string | undefined
  ): Promise<void> => {
    if (id) {
      setLoading(true);
      setAlertStatus('Rejected');
      let responseCode: number | null = null;
      try {
        if (!_.isEmpty(id)) {
          responseCode = await rejectRecurringFile(id, reason);
        }

        if (responseCode === STATUS_CODE_SUCCESS) {
          setAlertMessage(
            id.length > 1
              ? t('payment_file.rejected_multiple_alert', {
                  files: id.length,
                })
              : t('payment_file.rejected_alert')
          );
        }
        if (responseCode === STATUS_CODE_REJECT) {
          setAlertMessage(
            id.length > 1
              ? t('payment_file.already_approved_or_rejected_alert_multiple')
              : t('payment_file.already_approved_or_rejected_alert_single')
          );
        }
      } catch (error) {
        if (error !== STATUS_CODE_NOT_AUTHORIZED) {
          await getSearchRecurring(state.query);
        }
      } finally {
        setIsMultiSelect(false);
        state.handleOnClearMultipleSelectedQuery();
        await getSearchRecurring(state.query);
        setLoading(false);
      }
    }
  };
  useEffect(() => {
    if (
      alertMessage ||
      failedAlert.msg ||
      overdueAlert.msg ||
      tryagainAlert.msg
    ) {
      const timeout = setInterval(() => {
        clearInterval(timeout);
        setAlertMessage(undefined);
        setFailedAlert({ status: 'error', msg: '' });
        setOverdueAlert({ status: 'error', msg: '' });
        setTryAgainAlert({ status: 'error', msg: '' });
      }, ms('60s'));
    }
  }, [alertMessage, failedAlert.msg, overdueAlert.msg, tryagainAlert.msg]);

  const isCheckerPermission = useMemo(() => {
    const permissions = auth.currentUser.permissions;
    const checkerPermissions = [
      PAYMENT_CHECKER_O1_PERMISSION,
      PAYMENT_CHECKER_O2_PERMISSION,
    ];
    const userCheckerPermission = permissions?.find((permission: string) =>
      checkerPermissions.includes(permission)
    );
    return userCheckerPermission;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    state,
    isMultiSelect,
    setIsMultiSelect,
    handleMultipleSelection,
    handleCloseMultipleSelection,
    handleApproveRecurringFile,
    handleRejectRecurringFile,
    alertMessage,
    failedAlert,
    overdueAlert,
    tryagainAlert,
    alertStatus,
    isCheckerPermission,
    approvedRecurringCheckLists,
    recurringFile,
    auth,
  };
};

export default useViewModel;
